import {
  DistributionBarChart,
  MetricsCard,
  PageHeader,
  type SelectOption,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@getdevpulse/shared-ui"
import { Info } from "lucide-react"
import { observer } from "mobx-react-lite"
import { memo, useCallback, useMemo, useState } from "react"
import { SearchInput } from "../components/SearchInput"
import { useInfiniteScroll } from "../hooks/useVirtualization"
import { getYesterday } from "../lib/dateCalculations"
import { useDataStore, useI18nStore } from "../stores/StoreContext"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

interface RepositoryStats {
  repoName: string
  score: number
  prCount: number
  contributorCount: number
  scoreHistory: Array<{ label: string; value: number }>
  prHistory: Array<{ label: string; value: number }>
}

// Pre-computed card data to avoid inline calculations during render
interface RepositoryCardData {
  repoName: string
  displayValue: string
  percentage: number
  trend: "up" | "down" | "neutral"
  chartData: Array<{ label: string; value: number }>
  subtitle: string
}

// Memoized repository card with animation support
const RepositoryCard = memo(
  ({ data }: { data: RepositoryCardData }) => {
    // Extract organization name from repo name (format: org/repo)
    const getOrgName = (repoName: string): string | undefined => {
      const parts = repoName.split("/")
      return parts.length > 1 ? parts[0] : undefined
    }

    return (
      <div className="transition-all duration-300 ease-in-out">
        <MetricsCard
          title={data.repoName}
          description={data.displayValue}
          subtitle={data.subtitle}
          percentage={data.percentage}
          trend={data.trend}
          chartData={data.chartData}
          chartType="line"
          generativeAvatar={{
            name: data.repoName,
            type: "repository",
            organizationName: getOrgName(data.repoName),
          }}
        />
      </div>
    )
  },
  (prev, next) =>
    prev.data.repoName === next.data.repoName &&
    prev.data.displayValue === next.data.displayValue &&
    prev.data.percentage === next.data.percentage &&
    prev.data.trend === next.data.trend &&
    prev.data.subtitle === next.data.subtitle &&
    prev.data.chartData === next.data.chartData,
)

// Helper to get date range start based on time range
// Uses yesterday as the reference point to exclude the current incomplete day
const getDateRangeStart = (timeRange: TimeRange): Date => {
  const yesterday = getYesterday()

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // 7 days including yesterday
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // 14 days including yesterday
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // 28 days including yesterday
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (8 * 7 - 1) * 24 * 60 * 60 * 1000) // 8 weeks including yesterday
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (12 * 7 - 1) * 24 * 60 * 60 * 1000) // 12 weeks including yesterday
  }
  return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // Default to 7 days
}

// Helper to determine chart granularity and period based on available data
const getChartPeriod = (
  timeRange: TimeRange,
): { periods: number; unit: "day" | "week" | "month" } => {
  // For the new time ranges, use simple day-based periods
  if (timeRange === "7days") {
    return { periods: 7, unit: "day" }
  }
  if (timeRange === "14days") {
    return { periods: 14, unit: "day" }
  }
  if (timeRange === "28days") {
    return { periods: 28, unit: "day" }
  }
  if (timeRange === "8weeks") {
    return { periods: 8, unit: "week" }
  }
  if (timeRange === "12weeks") {
    return { periods: 12, unit: "week" }
  }
  return { periods: 7, unit: "day" } // Default
}

// Helper to format date labels
const formatDateLabel = (date: Date, unit: "day" | "week" | "month"): string => {
  if (unit === "month") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })
  }
  if (unit === "week") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

// Helper to get period start date
const getPeriodStart = (
  periodIndex: number,
  totalPeriods: number,
  unit: "day" | "week" | "month",
): Date => {
  const now = new Date()
  const periodOffset = totalPeriods - periodIndex - 1

  if (unit === "day") {
    return new Date(now.getTime() - periodOffset * 24 * 60 * 60 * 1000)
  }
  if (unit === "week") {
    return new Date(now.getTime() - periodOffset * 7 * 24 * 60 * 60 * 1000)
  }
  // month
  const date = new Date(now)
  date.setMonth(date.getMonth() - periodOffset)
  return date
}

export const RepositoriesPage = observer(() => {
  const dataStore = useDataStore()
  const i18nStore = useI18nStore()
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("7days")
  const [isNormalized, setIsNormalized] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  // Search handler
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query.toLowerCase())
  }, [])

  // Time range options for the dropdown (matching Dashboard)
  const timeRangeOptions: SelectOption[] = [
    { id: "7days", label: i18nStore.t("timeLast7Days") },
    { id: "14days", label: i18nStore.t("timeLast14Days") },
    { id: "28days", label: i18nStore.t("timeLast28Days") },
    { id: "8weeks", label: i18nStore.t("timeLast8Weeks") },
    { id: "12weeks", label: i18nStore.t("timeLast12Weeks") },
  ]

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    return getDateRangeStart(selectedTimeRange)
  }, [selectedTimeRange])

  // Get the timestamp for dependency tracking (Date objects don't work well in deps)
  const dateRangeTimestamp = dateRange.getTime()

  // Determine chart period configuration
  const chartConfig = useMemo(() => {
    return getChartPeriod(selectedTimeRange)
  }, [selectedTimeRange])

  // Calculate repository statistics with historical data
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable arrays require snapshot conversion via .slice()
  const repositoryStats = useMemo(() => {
    // Create a snapshot of the data to avoid MobX reactivity issues
    const commits = dataStore.allCommits.slice()
    const pullRequests = dataStore.allPullRequests.slice()

    const statsMap = new Map<string, RepositoryStats>()
    const contributorsByRepo = new Map<string, Set<string>>()
    const { periods, unit } = chartConfig

    // Initialize periods for all repositories
    const initializeRepository = (repoName: string): RepositoryStats => {
      const history: Array<{ label: string; value: number }> = []

      for (let i = 0; i < periods; i++) {
        const periodStart = getPeriodStart(i, periods, unit)
        history.push({
          label: formatDateLabel(periodStart, unit),
          value: 0,
        })
      }

      return {
        repoName,
        score: 0,
        prCount: 0,
        contributorCount: 0,
        scoreHistory: history.map((h) => ({ ...h })),
        prHistory: history.map((h) => ({ ...h })),
      }
    }

    // Helper to get period index for a date (using yesterday as reference)
    const getPeriodIndex = (date: Date): number => {
      const yesterday = getYesterday()
      let diff: number

      if (unit === "day") {
        diff = Math.floor((yesterday.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
      } else if (unit === "week") {
        diff = Math.floor((yesterday.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
      } else {
        // month
        const monthsDiff =
          (yesterday.getFullYear() - date.getFullYear()) * 12 +
          (yesterday.getMonth() - date.getMonth())
        diff = monthsDiff
      }

      const periodIndex = periods - diff - 1
      return periodIndex >= 0 && periodIndex < periods ? periodIndex : -1
    }

    // Process commits for scores
    commits.forEach((commit) => {
      if (commit.timestampDate >= dateRange) {
        const current = statsMap.get(commit.repoName) || initializeRepository(commit.repoName)
        current.score += commit.score || 0

        // Track unique contributors per repository
        if (!contributorsByRepo.has(commit.repoName)) {
          contributorsByRepo.set(commit.repoName, new Set())
        }
        contributorsByRepo.get(commit.repoName)?.add(commit.authorHandle)

        // Add to historical data
        const periodIndex = getPeriodIndex(commit.timestampDate)
        if (periodIndex >= 0) {
          current.scoreHistory[periodIndex].value += commit.score || 0
        }

        statsMap.set(commit.repoName, current)
      }
    })

    // Process PRs for counts (merged PRs)
    pullRequests.forEach((pr) => {
      if (pr.state === "merged" && pr.mergedAtDate && pr.mergedAtDate >= dateRange) {
        const current = statsMap.get(pr.repoName) || initializeRepository(pr.repoName)
        current.prCount += 1

        // Track unique contributors per repository
        if (!contributorsByRepo.has(pr.repoName)) {
          contributorsByRepo.set(pr.repoName, new Set())
        }
        contributorsByRepo.get(pr.repoName)?.add(pr.creatorHandle)

        // Add to historical data
        const periodIndex = getPeriodIndex(pr.mergedAtDate)
        if (periodIndex >= 0) {
          current.prHistory[periodIndex].value += 1
        }

        statsMap.set(pr.repoName, current)
      }
    })

    // Update contributor counts
    for (const [repoName, contributors] of contributorsByRepo.entries()) {
      const stats = statsMap.get(repoName)
      if (stats) {
        stats.contributorCount = contributors.size
      }
    }

    return Array.from(statsMap.values())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRangeTimestamp,
    chartConfig.periods,
    chartConfig.unit,
    // Force recalculation when data changes
    dataStore.allCommits.length,
    dataStore.allPullRequests.length,
  ])

  // Pre-compute all stats and card data for both absolute and normalized modes
  const { scoreCards, prCards, scoreStats, prStats } = useMemo(() => {
    if (repositoryStats.length === 0) {
      return {
        scoreCards: [],
        prCards: [],
        scoreStats: { sorted: [], average: 0, normalizedAverage: 0, total: 0, chartData: [] },
        prStats: { sorted: [], average: 0, normalizedAverage: 0, total: 0, chartData: [] },
      }
    }

    // Calculate totals and averages
    const totalScore = repositoryStats.reduce((sum, r) => sum + r.score, 0)
    const totalPRs = repositoryStats.reduce((sum, r) => sum + r.prCount, 0)
    const count = repositoryStats.length

    // Absolute averages
    const scoreAverage = totalScore / count
    const prAverage = totalPRs / count

    // Normalized averages (average of per-contributor values)
    const scoreNormalizedAverage =
      repositoryStats.reduce((sum, r) => sum + r.score / (r.contributorCount || 1), 0) / count
    const prNormalizedAverage =
      repositoryStats.reduce((sum, r) => sum + r.prCount / (r.contributorCount || 1), 0) / count

    // Compute absolute sorting (by raw values)
    const scoreSortedAbsolute = [...repositoryStats].sort((a, b) => b.score - a.score)
    const prSortedAbsolute = [...repositoryStats].sort((a, b) => b.prCount - a.prCount)

    // Compute normalized sorting (by value per contributor)
    const scoreSortedNormalized = [...repositoryStats].sort((a, b) => {
      const aValue = a.score / (a.contributorCount || 1)
      const bValue = b.score / (b.contributorCount || 1)
      return bValue - aValue
    })
    const prSortedNormalized = [...repositoryStats].sort((a, b) => {
      const aValue = a.prCount / (a.contributorCount || 1)
      const bValue = b.prCount / (b.contributorCount || 1)
      return bValue - aValue
    })

    // Create index maps for absolute and normalized orderings
    const scoreAbsoluteIndexMap = new Map(
      scoreSortedAbsolute.map((r, idx) => [r.repoName, idx + 1]),
    )
    const scoreNormalizedIndexMap = new Map(
      scoreSortedNormalized.map((r, idx) => [r.repoName, idx + 1]),
    )
    const prAbsoluteIndexMap = new Map(prSortedAbsolute.map((r, idx) => [r.repoName, idx + 1]))
    const prNormalizedIndexMap = new Map(prSortedNormalized.map((r, idx) => [r.repoName, idx + 1]))

    // Pick the active sorting based on isNormalized
    const scoreSorted = isNormalized ? scoreSortedNormalized : scoreSortedAbsolute
    const prSorted = isNormalized ? prSortedNormalized : prSortedAbsolute

    // Helper to compute trend
    const computeTrend = (
      value: number,
      average: number,
    ): { percentage: number; trend: "up" | "down" | "neutral" } => {
      const percentDiff = average > 0 ? ((value - average) / average) * 100 : 0
      const trend: "up" | "down" | "neutral" =
        percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"
      return { percentage: Number(percentDiff.toFixed(1)), trend }
    }

    // Helper function to filter out weekends with no activity from chart data
    // Only applies to day-based periods
    const filterWeekendInactivity = (
      chartData: Array<{ label: string; value: number }>,
    ): Array<{ label: string; value: number }> => {
      if (chartConfig.unit !== "day") {
        return chartData // Only filter for day-based periods
      }

      return chartData.filter((point, index) => {
        // Calculate the date for this period
        const periodStart = getPeriodStart(index, chartConfig.periods, chartConfig.unit)
        const dayOfWeek = periodStart.getDay()
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

        // Include if it's not a weekend, or if it's a weekend with activity
        return !isWeekend || point.value > 0
      })
    }

    // Create card data for score tab
    const scCards: RepositoryCardData[] = scoreSorted.map((repo, index) => {
      const contributorCount = repo.contributorCount || 1
      const displayValue = isNormalized ? repo.score / contributorCount : repo.score
      const avgValue = isNormalized ? scoreNormalizedAverage : scoreAverage
      const { percentage, trend } = computeTrend(displayValue, avgValue)

      const currentIndex = index + 1
      const absoluteIndex = scoreAbsoluteIndexMap.get(repo.repoName) || 0
      const normalizedIndex = scoreNormalizedIndexMap.get(repo.repoName) || 0
      const alternateIndex = isNormalized ? absoluteIndex : normalizedIndex
      const subtitle = `#${currentIndex} (${isNormalized ? "Abs" : "Norm"}: #${alternateIndex})`

      // Apply weekend filtering to chart data
      const rawChartData = isNormalized
        ? repo.scoreHistory.map((h) => ({ label: h.label, value: h.value / contributorCount }))
        : repo.scoreHistory

      return {
        repoName: repo.repoName,
        displayValue: Math.round(displayValue).toLocaleString(),
        percentage,
        trend,
        chartData: filterWeekendInactivity(rawChartData),
        subtitle,
      }
    })

    // Create card data for PR tab
    const pCards: RepositoryCardData[] = prSorted.map((repo, index) => {
      const contributorCount = repo.contributorCount || 1
      const displayValue = isNormalized ? repo.prCount / contributorCount : repo.prCount
      const avgValue = isNormalized ? prNormalizedAverage : prAverage
      const { percentage, trend } = computeTrend(displayValue, avgValue)

      const currentIndex = index + 1
      const absoluteIndex = prAbsoluteIndexMap.get(repo.repoName) || 0
      const normalizedIndex = prNormalizedIndexMap.get(repo.repoName) || 0
      const alternateIndex = isNormalized ? absoluteIndex : normalizedIndex
      const subtitle = `#${currentIndex} (${isNormalized ? "Abs" : "Norm"}: #${alternateIndex})`

      // Apply weekend filtering to chart data
      const rawChartData = isNormalized
        ? repo.prHistory.map((h) => ({ label: h.label, value: h.value / contributorCount }))
        : repo.prHistory

      return {
        repoName: repo.repoName,
        displayValue: displayValue.toFixed(1),
        percentage,
        trend,
        chartData: filterWeekendInactivity(rawChartData),
        subtitle,
      }
    })

    // Create chart data for distribution bar charts
    const scoreChartData = scoreSorted.map((repo) => {
      const contributorCount = repo.contributorCount || 1
      const value = isNormalized ? repo.score / contributorCount : repo.score
      return { name: repo.repoName, value: Number(value.toFixed(0)) }
    })

    const prChartData = prSorted.map((repo) => {
      const contributorCount = repo.contributorCount || 1
      const value = isNormalized ? repo.prCount / contributorCount : repo.prCount
      return { name: repo.repoName, value: Number(value.toFixed(1)) }
    })

    return {
      scoreCards: scCards,
      prCards: pCards,
      scoreStats: {
        sorted: scoreSorted,
        average: scoreAverage,
        normalizedAverage: scoreNormalizedAverage,
        total: totalScore,
        chartData: scoreChartData,
      },
      prStats: {
        sorted: prSorted,
        average: prAverage,
        normalizedAverage: prNormalizedAverage,
        total: totalPRs,
        chartData: prChartData,
      },
    }
  }, [repositoryStats, isNormalized])

  // Filter cards based on search query
  const filteredScoreCards = useMemo(() => {
    if (!searchQuery) return scoreCards
    return scoreCards.filter((card) => card.repoName.toLowerCase().includes(searchQuery))
  }, [scoreCards, searchQuery])

  const filteredPrCards = useMemo(() => {
    if (!searchQuery) return prCards
    return prCards.filter((card) => card.repoName.toLowerCase().includes(searchQuery))
  }, [prCards, searchQuery])

  // Use infinite scroll for each tab
  const {
    visibleItems: visibleScoreCards,
    sentinelRef: scoreSentinelRef,
    hasMore: hasMoreScore,
  } = useInfiniteScroll(filteredScoreCards)

  const {
    visibleItems: visiblePrCards,
    sentinelRef: prSentinelRef,
    hasMore: hasMorePr,
  } = useInfiniteScroll(filteredPrCards)

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("repositoriesTitle")}
          subtitle={i18nStore.t("repositoriesSubtitle")}
          selectOptions={timeRangeOptions}
          selectedOptionId={selectedTimeRange}
          onSelectChange={(id) => setSelectedTimeRange(id as TimeRange)}
          selectPlaceholder={i18nStore.t("selectTimeRange")}
          rightContent={
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                {isNormalized ? i18nStore.t("normalizedView") : i18nStore.t("absoluteView")}
                <span
                  title={i18nStore.t("normalizedViewTooltip")}
                  className="inline-flex items-center"
                >
                  <Info
                    className="h-4 w-4"
                    style={{ cursor: "pointer" }}
                    aria-label={i18nStore.t("normalizedViewTooltip")}
                  />
                </span>
              </span>
              <Switch
                checked={isNormalized}
                onCheckedChange={setIsNormalized}
                aria-label={i18nStore.t("normalizedViewLabel")}
              />
            </div>
          }
        >
          <Tabs defaultValue="score" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="score">{i18nStore.t("tabScore")}</TabsTrigger>
              <TabsTrigger value="prs">{i18nStore.t("tabPRs")}</TabsTrigger>
            </TabsList>

            {/* Search Input */}
            <div className="mt-4">
              <SearchInput
                placeholder={i18nStore.t("searchRepositories")}
                onSearch={handleSearch}
                className="max-w-md"
              />
            </div>

            {/* Score Tab */}
            <TabsContent value="score" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("repositoriesScoreTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("repositoriesScoreDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("scoreAverage", {
                      value: scoreStats.average.toFixed(0),
                    })}
                  </span>{" "}
                  {i18nStore.t("scoreComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={scoreStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="repository"
                getOrganizationName={(repoName) => {
                  const parts = repoName.split("/")
                  return parts.length > 1 ? parts[0] : undefined
                }}
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visibleScoreCards.map((cardData) => (
                  <RepositoryCard key={cardData.repoName} data={cardData} />
                ))}
              </div>

              {/* Sentinel for infinite scroll */}
              {hasMoreScore && <div ref={scoreSentinelRef} className="h-4" />}

              {filteredScoreCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {scoreStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("repositoriesNoData")}
                </div>
              )}
            </TabsContent>

            {/* PRs Tab */}
            <TabsContent value="prs" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("repositoriesPRsTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("repositoriesPRsDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("prsAverage", {
                      value: prStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("prsComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={prStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="repository"
                getOrganizationName={(repoName) => {
                  const parts = repoName.split("/")
                  return parts.length > 1 ? parts[0] : undefined
                }}
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visiblePrCards.map((cardData) => (
                  <RepositoryCard key={cardData.repoName} data={cardData} />
                ))}
              </div>

              {/* Sentinel for infinite scroll */}
              {hasMorePr && <div ref={prSentinelRef} className="h-4" />}

              {filteredPrCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {prStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("repositoriesNoData")}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PageHeader>
      </div>
    </div>
  )
})

RepositoriesPage.displayName = "RepositoriesPage"
